import React, { useEffect, useRef, useState } from "react";
import {
  format,
  startOfYear,
  eachDayOfInterval,
  endOfYear,
  isToday,
} from "date-fns";
import classNames from "classnames";

interface ActivityData {
  date: string;
  total_activities: string;
}

interface ActivitiesCalendarProps {
  activities: ActivityData[];
}

const ActivitiesCalendar: React.FC<ActivitiesCalendarProps> = ({
  activities,
}) => {
  const activityMap = new Map<string, number>();
  activities.forEach(({ date, total_activities }) => {
    activityMap.set(date, parseInt(total_activities, 10));
  });

  const yearStart = startOfYear(new Date());
  const yearEnd = endOfYear(new Date());
  const daysInYear = eachDayOfInterval({ start: yearStart, end: yearEnd });

  const months: { month: string; days: Date[] }[] = [];
  let currentMonthDays: Date[] = [];
  let currentMonth = format(daysInYear[0], "MMM yyyy");

  daysInYear.forEach((day) => {
    const monthYear = format(day, "MMM yyyy");
    if (monthYear !== currentMonth) {
      months.push({ month: currentMonth, days: currentMonthDays });
      currentMonthDays = [];
      currentMonth = monthYear;
    }
    currentMonthDays.push(day);
  });
  months.push({ month: currentMonth, days: currentMonthDays });

  const getCurrentMonthIndex = () => {
    const now = new Date();
    const currentMonthYear = format(now, "MMM yyyy");
    return months.findIndex(({ month }) => month === currentMonthYear);
  };

  const [currentMonthIndex, setCurrentMonthIndex] =
    useState(getCurrentMonthIndex);
  const [hoveredActivity, setHoveredActivity] = useState<{
    date: string;
    count: number;
    x: number;
    y: number;
  } | null>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  const getActivityLevel = (date: Date) => {
    const formattedDate = format(date, "yyyy-MM-dd");
    const activityCount = activityMap.get(formattedDate) || 0;
    if (activityCount > 20) return 4;
    if (activityCount > 10) return 3;
    if (activityCount > 5) return 2;
    if (activityCount > 0) return 1;
    return 0;
  };

  useEffect(() => {
    if (calendarRef.current) {
      const todayElement = calendarRef.current.querySelector(
        '[data-today="true"]'
      );
      if (todayElement) {
        const containerWidth = calendarRef.current.offsetWidth;
        const todayOffsetLeft = (todayElement as HTMLElement).offsetLeft;
        calendarRef.current.scrollLeft =
          todayOffsetLeft -
          containerWidth / 2 +
          (todayElement as HTMLElement).offsetWidth / 2;
      }
    }
  }, [currentMonthIndex]);

  const goToPreviousMonth = () => {
    setCurrentMonthIndex((prevIndex) => Math.max(prevIndex - 1, 0));
  };

  const goToNextMonth = () => {
    setCurrentMonthIndex((prevIndex) =>
      Math.min(prevIndex + 1, months.length - 1)
    );
  };

  const currentMonthData = months[currentMonthIndex];

  const handleMouseEnter = (
    event: React.MouseEvent<HTMLDivElement>,
    day: Date
  ) => {
    const activityCount = activityMap.get(format(day, "yyyy-MM-dd")) || 0;
    const { clientX, clientY } = event;

    // 툴팁이 화면을 벗어나지 않도록 조정
    const xOffset = clientX + 120 > window.innerWidth ? -120 : 10;
    const yOffset = clientY + 50 > window.innerHeight ? -50 : 10;

    setHoveredActivity({
      date: format(day, "yyyy-MM-dd"),
      count: activityCount,
      x: clientX + xOffset,
      y: clientY + yOffset,
    });
  };

  const handleMouseLeave = () => {
    setHoveredActivity(null);
  };

  return (
    <div className="relative w-full overflow-x-auto" ref={calendarRef}>
      <div className="flex flex-col items-start text-slate-700 dark:text-slate-300">
        <div className="flex items-center mb-4 px-2">
          <button
            onClick={goToPreviousMonth}
            className="mr-2 px-2 py-1 rounded hover:text-blue-500"
          >
            &lt; 전 달
          </button>
          <div className="text-lg font-bold flex-1 text-center">
            {currentMonthData.month}
          </div>
          <button
            onClick={goToNextMonth}
            className="ml-2 px-2 py-1 rounded hover:text-blue-500 "
          >
            다음달 &gt;
          </button>
        </div>

        <div className="grid grid-cols-7 gap-2 px-2">
          {Array.from({ length: 7 }).map((_, colIndex) => (
            <div key={colIndex} className="flex flex-col gap-2">
              {currentMonthData.days
                .filter((_, index) => index % 7 === colIndex)
                .map((day, dayIndex) => {
                  const activityLevel = getActivityLevel(day);
                  const dayClasses = classNames(
                    "w-6 h-6 rounded-sm flex items-center justify-center text-xs shadow-xl text-slate-700 dark:text-slate-500 font-bold cursor-pointer",
                    {
                      "bg-slate-300 dark:bg-slate-800": activityLevel === 0,
                      "bg-green-100": activityLevel === 1,
                      "bg-green-200": activityLevel === 2,
                      "bg-green-400": activityLevel === 3,
                      "bg-green-600": activityLevel === 4,
                    }
                  );

                  return (
                    <div
                      key={dayIndex}
                      className={dayClasses}
                      title={`${format(day, "yyyy-MM-dd")}: ${
                        activityMap.get(format(day, "yyyy-MM-dd")) || 0
                      } activities`}
                      data-today={isToday(day) ? "true" : "false"}
                      onMouseEnter={(e) => handleMouseEnter(e, day)}
                      onMouseLeave={handleMouseLeave}
                    >
                      {day.getDate()}
                    </div>
                  );
                })}
            </div>
          ))}
        </div>

        <div className="flex justify-end mt-4 px-2">
          <span className="text-xs text-gray-500 dark:text-slate-500 mr-2">
            적은 학습
          </span>
          <div className="w-4 h-4 bg-slate-300 dark:bg-slate-800 mx-1 rounded-sm"></div>
          <div className="w-4 h-4 bg-green-100 mx-1 rounded-sm"></div>
          <div className="w-4 h-4 bg-green-200 mx-1 rounded-sm"></div>
          <div className="w-4 h-4 bg-green-400 mx-1 rounded-sm"></div>
          <div className="w-4 h-4 bg-green-600 mx-1 rounded-sm"></div>
          <span className="text-xs text-gray-500 dark:text-slate-100 ml-2">
            많은 학습
          </span>
        </div>
      </div>

      {/* Tooltip */}
      {hoveredActivity && (
        <div
          className="fixed bg-black text-white text-xs rounded py-1 px-2 z-50 pointer-events-none"
          style={{
            top: `${hoveredActivity.y}px`,
            left: `${hoveredActivity.x}px`,
          }}
        >
          {hoveredActivity.date}: {hoveredActivity.count} 학습
        </div>
      )}
    </div>
  );
};

export default ActivitiesCalendar;
